import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AdvertiserSelect from "./components/AdvertiserSelect";
import CampaignCreationSlideContainer from "../../../components/CampaignCreation/CampaignCreationSlideContainer";
import PublicationListsContent from "./components/PublicationListsContent";
import { CAMPAIGN_STEPS } from "../../../types/campaignTypes";
import { PageStepComponentProps } from "../../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { basicSteps } from "../../../constants/steps";
import { createDomainList, getCustomDomainLists, getPublicDomainLists, updateDomainList } from "./publicationListApi";
import { errorToaster } from "../../../helpers/toasters";
import {
  setCampaignToCreateSelectedAdvertiser,
  setCustomDomainLists,
  setPublicDomainLists,
  setSelectedDomainIds as setSelectedDomainIdsAction,
  setSelectedDomainListsIds as setSelectedDomainListsIdsAction,
  setSelectedDomainNames as setSelectedDomainNamesAction,
} from "../../../redux/reducers/campaignToCreate";

const isOnlyPublicationListsStep = (steps: CAMPAIGN_STEPS[] | null | undefined): boolean => {
  if (!steps?.length) return false;
  const nonBaseSteps = steps.filter((step) => !basicSteps.includes(step));
  return nonBaseSteps.length === 1 && nonBaseSteps[0] === CAMPAIGN_STEPS.PUBLICATION_LISTS;
};

const PublicationListSelectionSlide: FC<PageStepComponentProps> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
}) => {
  const dispatch = useDispatch();

  const brandgenAdvertiserId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser?.brandgenAdvertiserId,
  )!;
  const publicDomainLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.publicDomainLists);
  const customDomainLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.customDomainLists);
  const campaignSteps = useSelector((state: ReduxApplicationState) => state.campaignToCreate.campaignSteps);
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const selectedDomainNamesFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainNames,
  );
  const selectedDomainListsIdsFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainListsIds,
  );
  const selectedDomainIdsFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainIds,
  );

  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>(selectedDomainIdsFromStore ?? []);
  const [selectedDomainNames, setSelectedDomainNames] = useState<string[]>(selectedDomainNamesFromStore ?? []);
  const [selectedDomainListsIds, setSelectedDomainListsIds] = useState<string[]>(selectedDomainListsIdsFromStore ?? []);
  const [isLoadingDomainLists, setIsLoadingDomainLists] = useState(false);
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState("");

  const isSplitMode = useMemo(() => isOnlyPublicationListsStep(campaignSteps), [campaignSteps]);

  const allLists = useMemo(() => [...publicDomainLists, ...customDomainLists], [publicDomainLists, customDomainLists]);

  useEffect(() => {
    setSelectedDomainListsIds(selectedDomainListsIdsFromStore ?? []);
    setSelectedDomainIds(selectedDomainIdsFromStore ?? []);
    setSelectedDomainNames(selectedDomainNamesFromStore ?? []);
  }, [selectedDomainListsIdsFromStore, selectedDomainIdsFromStore, selectedDomainNamesFromStore]);

  useEffect(() => {
    if (allLists.length === 0 || selectedDomainIds.length === 0 || selectedDomainListsIds.length > 0) {
      return;
    }
    const listIdsFromDomains = new Set<string>();
    for (const list of allLists) {
      const domainIdsInList = new Set((list.listDomains ?? []).map((d) => d.id));
      const hasSelectedDomain = selectedDomainIds.some((id) => domainIdsInList.has(id));
      if (hasSelectedDomain) {
        listIdsFromDomains.add(list.id);
      }
    }
    if (listIdsFromDomains.size > 0) {
      setSelectedDomainListsIds((prev) => [...new Set([...prev, ...listIdsFromDomains])]);
    }
  }, [allLists, selectedDomainIds, selectedDomainListsIds.length]);

  const toggleSelectDomainLists = useCallback(
    (toggledListId: string) => {
      const list = allLists.find((l) => l.id === toggledListId);
      const activeDomains = list?.listDomains?.filter((d) => d.active) ?? [];

      if (selectedDomainListsIds.includes(toggledListId)) {
        setSelectedDomainListsIds((ids) => ids.filter((id) => id !== toggledListId));
        setSelectedDomainNames((names) => names.filter((n) => !activeDomains.some((d) => d.name === n)));
        setSelectedDomainIds((ids) => ids.filter((id) => !activeDomains.some((d) => d.id === id)));
      } else {
        setSelectedDomainListsIds((ids) => [...ids, toggledListId]);
        setSelectedDomainIds((ids) => [...new Set([...ids, ...activeDomains.map((d) => d.id)])]);
        setSelectedDomainNames((names) => [...new Set([...names, ...activeDomains.map((d) => d.name)])]);
      }
    },
    [allLists, selectedDomainListsIds],
  );

  const toggleSelectDomain = useCallback(
    (domainId: string) => {
      const domain = allLists.flatMap((l) => l.listDomains ?? []).find((d) => d.id === domainId);
      const name = domain?.name;
      const isRemoving = selectedDomainIds.includes(domainId);

      setSelectedDomainIds((ids) => (isRemoving ? ids.filter((id) => id !== domainId) : [...ids, domainId]));
      if (name) {
        setSelectedDomainNames((names) =>
          isRemoving ? names.filter((n) => n !== name) : [...new Set([...names, name])],
        );
      }
    },
    [allLists, selectedDomainIds],
  );

  useEffect(() => {
    if (publicDomainLists.length > 0) return;

    const load = async () => {
      try {
        setIsLoadingDomainLists(true);
        const publicLists = await getPublicDomainLists();
        dispatch(setPublicDomainLists(publicLists));
        if (brandgenAdvertiserId) {
          const customLists = await getCustomDomainLists(brandgenAdvertiserId);
          dispatch(setCustomDomainLists(customLists));
        }
      } catch (error) {
        console.error("getDomainListsAndSetState error:", error);
      } finally {
        setIsLoadingDomainLists(false);
      }
    };
    load();
  }, [brandgenAdvertiserId, dispatch, publicDomainLists.length]);

  useEffect(() => {
    if (!isAttemptingToGoNext || !setIsAttemptingToGoNext || !handleNext) return;

    const hasSelection = isSplitMode ? selectedDomainIds.length > 0 : selectedDomainListsIds.length > 0;

    if (hasSelection) {
      dispatch(setSelectedDomainNamesAction(selectedDomainNames));
      dispatch(setSelectedDomainListsIdsAction(selectedDomainListsIds));
      dispatch(setSelectedDomainIdsAction(selectedDomainIds));
      handleNext();
      setIsAttemptingToGoNext(false);
    } else {
      errorToaster("Please, select required domain lists.", 5000);
      setIsAttemptingToGoNext(false);
    }
  }, [
    dispatch,
    handleNext,
    isAttemptingToGoNext,
    isSplitMode,
    selectedDomainIds,
    selectedDomainListsIds,
    selectedDomainNames,
    setIsAttemptingToGoNext,
  ]);

  useEffect(() => {
    if (!selectedAdvertiserId && setIsAttemptingToGoNext) return;

    const advertiser = advertisers.find((a) => a.brandgenAdvertiserId === selectedAdvertiserId);
    if (advertiser) dispatch(setCampaignToCreateSelectedAdvertiser(advertiser));

    if (!selectedAdvertiserId) return;

    const loadCustom = async () => {
      try {
        setIsLoadingDomainLists(true);
        const lists = await getCustomDomainLists(selectedAdvertiserId);
        dispatch(setCustomDomainLists(lists));
      } catch (error) {
        console.error("getCustomDomainListsAndSetState error:", error);
      } finally {
        setIsLoadingDomainLists(false);
      }
    };
    loadCustom();
  }, [advertisers, dispatch, selectedAdvertiserId, setIsAttemptingToGoNext]);

  const slideHeading = isSplitMode ? "Select Publication Lists:" : "Select or Create Publication Lists:";
  const showAdvertiserSelect = !setIsAttemptingToGoNext;
  const showInContainer = Boolean(setIsAttemptingToGoNext);

  return (
    <div style={{ padding: showAdvertiserSelect ? "4rem" : 0 }}>
      {showAdvertiserSelect && (
        <AdvertiserSelect advertisers={advertisers} value={selectedAdvertiserId} onChange={setSelectedAdvertiserId} />
      )}
      {showInContainer ? (
        <CampaignCreationSlideContainer heading={slideHeading}>
          <PublicationListsContent
            isSplitMode={showInContainer}
            isLoading={isLoadingDomainLists}
            publicDomainLists={publicDomainLists}
            customDomainLists={customDomainLists}
            selectedDomainListsIds={selectedDomainListsIds}
            selectedDomainIds={selectedDomainIds}
            toggleSelectDomainLists={toggleSelectDomainLists}
            toggleSelectDomain={toggleSelectDomain}
            updateDomainList={updateDomainList}
            createDomainList={createDomainList}
          />
        </CampaignCreationSlideContainer>
      ) : (
        <PublicationListsContent
          isSplitMode={isSplitMode}
          isLoading={isLoadingDomainLists}
          publicDomainLists={publicDomainLists}
          customDomainLists={customDomainLists}
          selectedDomainListsIds={selectedDomainListsIds}
          selectedDomainIds={selectedDomainIds}
          toggleSelectDomainLists={toggleSelectDomainLists}
          toggleSelectDomain={toggleSelectDomain}
          updateDomainList={updateDomainList}
          createDomainList={createDomainList}
        />
      )}
    </div>
  );
};

export default PublicationListSelectionSlide;
